import { useState, type ReactElement, type ReactNode } from 'react';
import { Form, useActionData, useNavigation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert, Button, FieldError, invalidProps } from '@marketplace/ui';
import {
  ProviderKindSchema,
  type CategorySummary,
  type ProviderProfileOwn,
} from '@marketplace/contracts';
import { DEFAULT_RADIUS_KM, type ProviderFormField } from './signup.js';

/**
 * The provider profile form — `W2-T05` §3.5, §7. **One component for becoming a provider and for
 * editing the profile**, so the two cannot drift: the same fields, the same parser
 * (`parseProviderForm`), the same errors. What differs is passed in: the values it starts with, the
 * button, and what the page says around it.
 *
 * Inputs are uncontrolled with `defaultValue`/`defaultChecked`, as `/jobs/new` does: a refused submit
 * does not remount the page, so what was typed survives.
 */
export type ProviderFormActionData =
  { errors: Partial<Record<ProviderFormField, string>> } | { failure: string } | { saved: true };

export interface ProviderFormProps {
  categories: CategorySummary[];
  /** The stored profile when editing; absent when becoming a provider. */
  initial?: ProviderProfileOwn;
  testId: string;
  submitLabel: string;
  pendingLabel: string;
  /** Shown under the kind choice — the edit page says what switching does and does not unlock. */
  kindNote?: ReactNode;
}

export function ProviderForm({
  categories,
  initial,
  testId,
  submitLabel,
  pendingLabel,
  kindNote,
}: ProviderFormProps): ReactElement {
  const { t } = useTranslation();
  const result = useActionData<ProviderFormActionData>();
  const navigation = useNavigation();

  const errors = result !== undefined && 'errors' in result ? result.errors : {};
  const failure = result !== undefined && 'failure' in result ? result.failure : null;
  const saved = result !== undefined && 'saved' in result;
  const chosen = new Set(initial?.categories.map((category) => category.slug));
  const address = initial?.baseAddress;

  // `W3-T08` §3.4: the ticked trades, tracked only so the licence notice can follow them. The
  // checkboxes stay uncontrolled — what is submitted is still the form's own state.
  const [ticked, setTicked] = useState<ReadonlySet<string>>(chosen);
  const invited = categories.filter(
    (category) => category.requiresLicence && ticked.has(category.slug),
  );

  function tick(slug: string, checked: boolean): void {
    setTicked((previous) => {
      const next = new Set(previous);
      if (checked) next.add(slug);
      else next.delete(slug);
      return next;
    });
  }

  /** A labelled text input with its error, the shape the name and address fields share. */
  function field(
    name: 'displayName' | 'line1' | 'postalCode' | 'city' | 'province',
    options: {
      maxLength: number;
      autoComplete: string;
      defaultValue: string | undefined;
      inputMode?: 'numeric';
    },
  ): ReactElement {
    const id = `pro-${name}`;
    return (
      <>
        <label htmlFor={id}>{t(`proForm.field.${name}`)}</label>
        <input
          id={id}
          name={name}
          maxLength={options.maxLength}
          autoComplete={options.autoComplete}
          defaultValue={options.defaultValue ?? ''}
          {...(options.inputMode === undefined ? {} : { inputMode: options.inputMode })}
          {...invalidProps(errors[name], `${id}-error`)}
        />
        <FieldError
          id={`${id}-error`}
          message={errors[name] === undefined ? undefined : t(errors[name])}
        />
      </>
    );
  }

  return (
    <Form method="post" className="mp-form mp-stack-form" data-testid={testId} noValidate>
      {field('displayName', {
        maxLength: 120,
        autoComplete: 'organization',
        defaultValue: initial?.displayName,
      })}

      <fieldset {...invalidProps(errors.kind, 'pro-kind-error')}>
        <legend>{t('proForm.field.kind')}</legend>
        {ProviderKindSchema.options.map((kind) => (
          <label key={kind} className="mp-check">
            <input type="radio" name="kind" value={kind} defaultChecked={initial?.kind === kind} />{' '}
            {t(`proForm.kind.${kind}`)}
          </label>
        ))}
        {kindNote === undefined ? null : <p className="mp-meta">{kindNote}</p>}
      </fieldset>
      <FieldError
        id="pro-kind-error"
        message={errors.kind === undefined ? undefined : t(errors.kind)}
      />

      <fieldset {...invalidProps(errors.categories, 'pro-categories-error')}>
        <legend>{t('proForm.field.categories')}</legend>
        {categories.length === 0 ? (
          <p className="mp-meta">{t('jobForm.categories.unavailable')}</p>
        ) : (
          categories.map((category) => (
            <label key={category.slug} className="mp-check">
              <input
                type="checkbox"
                name="categories"
                value={category.slug}
                defaultChecked={chosen.has(category.slug)}
                onChange={(event) => {
                  tick(category.slug, event.currentTarget.checked);
                }}
              />{' '}
              {category.name}
            </label>
          ))
        )}
      </fieldset>
      <FieldError
        id="pro-categories-error"
        message={errors.categories === undefined ? undefined : t(errors.categories)}
      />
      {/*
       * `W3-T08` §3.4 — a gated trade invites proof; it never requires it. No link and no button:
       * the upload is `W8-T01`, which adds its action here, and a call to action that goes nowhere
       * is worse than silence. Never "unverified" or "pending" either — a missing badge is the only
       * such signal the platform gives (§1).
       */}
      {invited.length === 0 ? null : (
        <p className="mp-meta" data-testid="licence-invited">
          {t('providers.licence.invited', {
            trades: invited.map((category) => category.name).join(', '),
          })}
        </p>
      )}

      <label htmlFor="pro-bio">{t('proForm.field.bio')}</label>
      <textarea
        id="pro-bio"
        name="bio"
        rows={4}
        maxLength={2000}
        defaultValue={initial?.bio ?? ''}
        {...invalidProps(errors.bio, 'pro-bio-error')}
      />
      <FieldError
        id="pro-bio-error"
        message={errors.bio === undefined ? undefined : t(errors.bio)}
      />

      <fieldset>
        <legend>{t('proForm.field.address')}</legend>
        <p className="mp-meta">{t('proForm.address.hint')}</p>
        {field('line1', {
          maxLength: 200,
          autoComplete: 'address-line1',
          defaultValue: address?.line1,
        })}
        {field('postalCode', {
          maxLength: 5,
          autoComplete: 'postal-code',
          inputMode: 'numeric',
          defaultValue: address?.postalCode,
        })}
        {field('city', {
          maxLength: 100,
          autoComplete: 'address-level2',
          defaultValue: address?.city,
        })}
        {field('province', {
          maxLength: 100,
          autoComplete: 'address-level1',
          defaultValue: address?.province,
        })}
      </fieldset>

      <label htmlFor="pro-radius">{t('proForm.field.radius')}</label>
      <input
        id="pro-radius"
        name="radiusKm"
        inputMode="numeric"
        defaultValue={String(
          initial?.serviceRadiusMetres == null
            ? DEFAULT_RADIUS_KM
            : Math.round(initial.serviceRadiusMetres / 1000),
        )}
        autoComplete="off"
        {...invalidProps(errors.radius, 'pro-radius-error')}
      />
      <FieldError
        id="pro-radius-error"
        message={errors.radius === undefined ? undefined : t(errors.radius)}
      />

      <label htmlFor="pro-rate">{t('proForm.field.rate')}</label>
      <p className="mp-meta">{t('proForm.rate.hint')}</p>
      <input
        id="pro-rate"
        name="rate"
        inputMode="decimal"
        autoComplete="off"
        defaultValue={
          initial?.hourlyRateCents == null
            ? ''
            : (initial.hourlyRateCents / 100).toFixed(2).replace('.', ',')
        }
        {...invalidProps(errors.rate, 'pro-rate-error')}
      />
      <FieldError
        id="pro-rate-error"
        message={errors.rate === undefined ? undefined : t(errors.rate)}
      />

      {failure === null ? null : <Alert tone="error">{t(failure)}</Alert>}
      {saved ? <Alert tone="success">{t('proForm.saved')}</Alert> : null}

      <Button
        type="submit"
        variant="primary"
        isPending={navigation.state === 'submitting'}
        pendingLabel={pendingLabel}
      >
        {submitLabel}
      </Button>
    </Form>
  );
}
